import { http } from '@/app/lib/http/client'

export interface PromotionPricingRule {
  source?: 'base_price' | 'quantity_tier' | 'promotion' | string
  quantity_tier_id?: number | null
  discount_percentage?: string | number
  promotion_id?: number | null
  promotion_name?: string | null
  promotion_type?: 'percentage' | 'fixed_amount' | 'buy_x_pay_y' | null
  value?: string | number | null
  fixed_application?: 'per_unit' | 'per_line' | null
  buy_quantity?: number | null
  pay_quantity?: number | null
  channel?: 'pos' | 'ecommerce' | 'both' | null
  starts_at?: string | null
  ends_at?: string | null
}

export interface PricingAmounts {
  subtotal?: string | number
  discount_total?: string | number
  taxable_subtotal?: string | number
  tax_total?: string | number
  regular_unit_subtotal?: string | number
  regular_unit_total?: string | number
  applied_unit_subtotal?: string | number
  applied_unit_tax?: string | number
  applied_unit_total?: string | number
  total?: string | number
  regular_total?: string | number
  savings_total?: string | number
}

export interface Product {
  id: number
  name: string
  description?: string | null
  code: string
  price: number
  stock: number
  category?: string
  department?: string
  requiresPrescription: boolean
  requiresLot?: boolean
  productType?: string
  regulatoryClass?: RegulatoryClass | null
  image?: string
  promotion?: string
  promotionDiscount?: number
  promotionExpiry?: string
  pricing?: PromotionPricingRule | null
  amounts?: PricingAmounts
  stocks?: Array<{ sucursal_id: number; sucursal_name: string; stock: number }>
  priceTiers?: Array<{
    id: number
    minQuantity: number
    discountPercentage: number
    startsAt: string
    endsAt: string
    isActive: boolean
    isEffective?: boolean
    lifecycleStatus?: string
  }>
}

export interface CartItem extends Product {
  sku?: string
  barcode?: string
  quantity: number
  version: number
  selected: boolean
  cart_item_id?: number
  product_id?: number
  pricing?: PromotionPricingRule | null
  amounts?: PricingAmounts
  total?: string | number
  issues?: Array<{ code: string; message: string }>
  is_valid?: boolean
  prescriptionRequired?: boolean
  prescriptionCovered?: boolean
  prescriptionId?: number | null
  allocations?: InventoryAllocation[]
}

export interface InventoryAllocation {
  source_type: 'unbatched' | 'batch'
  batch_id: number | null
  batch_number?: string | null
  expiration_date?: string | null
  quantity: number
}

export interface InventorySource extends InventoryAllocation {
  is_expired: boolean
  physical: number
  reserved: number
  available: number
  reserved_by_cart: number
  reserved_other: number
  available_to_cart: number
}

export interface InventorySourcesSnapshot {
  product_id: number
  requires_lot: boolean
  physical: number
  batch_physical: number
  free_physical: number
  reserved: number
  sellable: number
  reserved_by_cart: number
  reserved_other: number
  sellable_to_cart: number
  excluded_expired_physical?: number
  excluded_expired_reserved_by_cart?: number
  requested_quantity: number
  sources: InventorySource[]
  current_allocations: InventoryAllocation[]
  proposed_allocations: InventoryAllocation[]
}

export type RegulatoryClass = 'GENERAL' | 'ANTIBIOTIC' | 'CONTROLLED'

export interface Doctor {
  id: number
  full_name: string
  professional_license: string
  address_line1: string
  address_line2: string | null
  city: string
  state: string
  zip: string
  country: string
  is_active?: boolean
}

export interface CartPrescription {
  id: number
  doctor: Doctor
  prescription_date?: string | null
  fulfillment_type: 'PARTIAL' | 'COMPLETE'
  status: 'DRAFT' | 'DISPENSED' | 'VOIDED'
  cart_item_ids: number[]
  controls: Array<{
    regulatory_class: RegulatoryClass
    folio?: string | null
    folio_source?: 'AUTOMATIC' | 'MANUAL' | null
  }>
}

export interface PosCartData {
  id: number | null
  items: CartItem[]
  totals: {
    subtotal: string
    discount_total: string
    taxable_subtotal: string
    tax_total: string
    total: string
    regular_total?: string
    savings_total?: string
  }
  is_valid: boolean
  prescriptions: CartPrescription[]
  regulatory?: {
    requires_prescription: boolean
    required_items_count: number
    covered_items_count: number
    prescriptions_count: number
    is_ready_for_checkout: boolean
  }
  updated_at?: string | null
}

export function posCartRevision(cart?: PosCartData): string | null {
  if (!cart) return null

  const items = cart.items
    .map((item) => ({
      cartItemId: item.cart_item_id ?? null,
      productId: item.product_id ?? item.id,
      version: item.version,
      quantity: item.quantity,
      total: String(item.amounts?.total ?? item.total ?? ''),
    }))
    .sort((left, right) => {
      const leftId = left.cartItemId ?? left.productId
      const rightId = right.cartItemId ?? right.productId

      return leftId - rightId
    })

  return JSON.stringify({
    id: cart.id,
    items,
    totals: {
      subtotal: cart.totals.subtotal,
      discountTotal: cart.totals.discount_total,
      taxableSubtotal: cart.totals.taxable_subtotal,
      taxTotal: cart.totals.tax_total,
      total: cart.totals.total,
      regularTotal: cart.totals.regular_total,
      savingsTotal: cart.totals.savings_total,
    },
  })
}

export const POS_CART_REFRESH_INTERVAL_MS = 10_000

export function posCartRefetchInterval(
  cart?: PosCartData,
): number | false {
  return cart?.items?.length ? POS_CART_REFRESH_INTERVAL_MS : false
}

export function posCartExpirationDelay(
  cart: PosCartData | undefined,
  now = Date.now(),
): number | null {
  const nearestExpiration = cart?.items
    ?.map((item) => item.pricing?.ends_at)
    .filter((value): value is string => Boolean(value))
    .map((value) => new Date(value).getTime())
    .filter((value) => Number.isFinite(value) && value > now)
    .sort((left, right) => left - right)[0]

  if (!nearestExpiration) return null

  return Math.min(nearestExpiration - now + 50, 2_147_483_647)
}

export function normalizePosCart(cart: any): PosCartData {
  if (!cart || !Array.isArray(cart.items)) return cart

  return {
    ...cart,
    prescriptions: Array.isArray(cart.prescriptions) ? cart.prescriptions : [],
    items: cart.items.map((item: any) => {
      const product = item.product || {}
      const pricing = item.pricing || {}
      const amounts = item.amounts || {}

      return {
        ...item,
        id: item.product_id,
        product_id: item.product_id,
        cart_item_id: item.id,
        name: product.name || 'N/A',
        code: product.sku || product.barcode || 'N/A',
        sku: product.sku,
        barcode: product.barcode,
        stock: item.available_stock || 0,
        regulatoryClass: item.regulatory_class || product.product_type?.regulatory_class || null,
        requiresPrescription: Boolean(item.prescription_required || product.product_type?.requires_prescription),
        requiresLot: Boolean(product.product_type?.requires_lot),
        prescriptionRequired: Boolean(item.prescription_required),
        prescriptionCovered: Boolean(item.prescription_covered),
        prescriptionId: item.prescription_id ? Number(item.prescription_id) : null,
        selected: Boolean(item.selected),
        category:
          product.category_name ||
          (typeof product.category === 'object'
            ? product.category?.name
            : product.category) ||
          'N/A',
        department:
          typeof product.department === 'object'
            ? product.department?.name
            : product.department,
        image: product.image_url || product.image,
        price: Number(
          amounts.applied_unit_total ||
          pricing.unit_price ||
          item.unit_price ||
          product.price ||
          0,
        ),
        pricing,
        promotionDiscount: pricing.discount_percentage || 0,
        promotion: pricing.source === 'promotion' ? pricing.promotion_name : undefined,
        promotionExpiry: pricing.source === 'promotion' ? pricing.ends_at : undefined,
      }
    }),
  }
}

export function cartFromValidationError(error: any): PosCartData | null {
  const cart = error?.response?.data?.errors?.cart

  return cart ? normalizePosCart(cart) : null
}

export interface PosUseCase {
  id: number
  name: string
  slug: string
  description?: string
  sort_order: number
}

interface PosProductLine {
  product: {
    id: number
    name: string
    sku: string
    image_url?: string | null
    category?: { name: string } | null
    product_type?: { code: string; requires_lot?: boolean; requires_prescription: boolean; regulatory_class?: RegulatoryClass | null } | null
  }
  available_stock: number
  amounts?: { applied_unit_total?: string | number }
}

export interface CreateCustomerPayload {
  name: string
  phone: string
  email?: string
  notes?: string
  is_active?: boolean
}

export interface DeliveryDetails {
  deliveryUserId: number | null
  deliveryPerson?: string
  recipientName: string
  customerPhone: string
  customerAddress: string
  observations: string
  deliveryCost: number
}

export interface CashCapacity {
  limit_amount: string
  balance_amount: string
  remaining_amount: string
  excess_amount: string
  usage_percentage: string
  status: 'NORMAL' | 'WARNING' | 'CRITICAL'
  label: string
  message: string | null
  is_over_limit: boolean
  can_receive_cash: boolean
}

export interface PosActor {
  id: number | null
  source_user_id?: number
  name: string
  last_name?: string | null
  full_name?: string
  is_deleted?: boolean
}

export interface PosSeller {
  id: number
  name: string
  last_name?: string | null
  role_name: string
}

export interface PosRegisterContext {
  device: { id: number; name: string }
  sucursal: { id: number; name: string }
  cash_register: {
    id: number
    name: string
    code: string
    cash_limit_amount: string
    is_active: boolean
  } | null
  session: {
    id: number
    status: string
    opening_cash_amount: string
    cash_balance_amount: string
    cash_capacity: CashCapacity
    opened_by?: { id: number; name: string; last_name?: string }
    seller?: PosActor | null
    seller_assigned_by?: PosActor | null
    seller_assigned_at?: string | null
    seller_is_valid?: boolean
    sale_blocker_code?: 'SELLER_REQUIRED' | 'SELLER_INVALID' | null
  } | null
  cash_capacity: CashCapacity | null
  can_sell: boolean
  seller: PosActor | null
  seller_assigned_by: PosActor | null
  seller_assigned_at: string | null
  can_complete_sale: boolean
  sale_blocker_code: 'SELLER_REQUIRED' | 'SELLER_INVALID' | null
  register_pin_configured: boolean
}

// ----------------------------------------------------------------------
// DATA MOCK (Comentada para referencia)
// ----------------------------------------------------------------------

export const posProductService = {
  async getProducts(
    params: { search?: string; use_case_id?: number; page?: number; per_page?: number } = {},
    signal?: AbortSignal,
  ) {
    const response = await http.get('/pos/products', { params, signal })
    const paginator = response.data?.data || response.data || {}
    const items = paginator.data || (Array.isArray(paginator) ? paginator : [])

    return {
      ...paginator,
      data: items.map((item: any) => {
        const product = item.product || item
        const typeCode = product.product_type?.code || ''
        const regulatoryClass = product.product_type?.regulatory_class || null
        const productType = typeCode === 'CONTROLLED'
          ? 'controlled'
          : typeCode === 'ANTIBIOTIC'
            ? 'antibiotic'
            : 'otc'

        return {
          id: product.id,
          name: product.name,
          description: product.description ?? null,
          code: product.barcode || product.sku || '',
          price: Number(item.amounts?.total ?? product.price?.final_price ?? product.price?.sale_price ?? product.price?.cost_with_taxes ?? product.price ?? 0),
          stock: item.available_stock ?? product.stocks?.reduce((sum: number, s: any) => sum + s.stock, 0) ?? product.available_stock ?? 0,
          stocks: product.stocks?.map((s: any) => ({
            sucursal_id: s.sucursal_id,
            sucursal_name: s.sucursal_name || s.sucursal?.name || `Sucursal ${s.sucursal_id}`,
            stock: s.stock
          })) || [],
          category: product.category?.name || product.category || '',
          department: product.department?.name || product.department || 'Sin departamento',
          regulatoryClass,
          requiresPrescription: Boolean(product.product_type?.requires_prescription),
          requiresLot: Boolean(product.product_type?.requires_lot),
          productType,
          image: product.hero_image || product.hero_image_url || product.image_url || undefined,
          pricing: item.pricing || null,
          amounts: item.amounts || undefined,
          promotion: item.pricing?.source === 'promotion' ? item.pricing.promotion_name : undefined,
          promotionDiscount: item.pricing?.discount_percentage || 0,
          promotionExpiry: item.pricing?.source === 'promotion' ? item.pricing.ends_at : undefined,
          priceTiers: (item.quantity_tiers || product.price_tiers)?.map((t: any) => ({
            id: t.id,
            minQuantity: t.min_quantity,
            discountPercentage: t.discount_percentage,
            startsAt: t.starts_at,
            endsAt: t.ends_at,
            isActive: t.is_active ?? true,
            isEffective: t.is_effective ?? true,
            lifecycleStatus: t.lifecycle_status
          })) || [],
        } as Product
      }),
    }
  },
  async getUseCases() {
    const response = await http.get('/pos/use-cases')
    return (response.data?.data || []) as PosUseCase[]
  },

  async priceCheckByBarcode(barcode: string, signal?: AbortSignal) {
    const response = await http.get('/pos/price-check/products', {
      params: { barcode },
      signal,
    })

    const item = response.data?.data || response.data
    const product = item.product || item
    const typeCode = product.product_type?.code || ''
    const regulatoryClass = product.product_type?.regulatory_class || null
    const productType = typeCode === 'CONTROLLED'
      ? 'controlled'
      : typeCode === 'ANTIBIOTIC'
        ? 'antibiotic'
        : 'otc'

    return {
      id: product.id,
      name: product.name,
      description: product.description ?? null,
      code: product.barcode || product.sku || '',
      price: Number(item.amounts?.total ?? product.price?.final_price ?? product.price?.sale_price ?? product.price?.cost_with_taxes ?? product.price ?? 0),
      stock: item.available_stock ?? product.stocks?.reduce((sum: number, s: any) => sum + s.stock, 0) ?? product.available_stock ?? 0,
      stocks: product.stocks?.map((s: any) => ({
        sucursal_id: s.sucursal_id,
        sucursal_name: s.sucursal_name || s.sucursal?.name || `Sucursal ${s.sucursal_id}`,
        stock: s.stock
      })) || [],
      category: product.category?.name || product.category || '',
      department: product.department?.name || product.department || 'Sin departamento',
      presentation: product.presentation || null,
      regulatoryClass,
      requiresPrescription: Boolean(product.product_type?.requires_prescription),
      requiresLot: Boolean(product.product_type?.requires_lot),
      productType,
      image: product.hero_image || product.hero_image_url || product.image_url || undefined,
      pricing: item.pricing || null,
      amounts: item.amounts || undefined,
      promotion: item.pricing?.source === 'promotion' ? item.pricing.promotion_name : undefined,
      promotionDiscount: item.pricing?.discount_percentage || 0,
      promotionExpiry: item.pricing?.source === 'promotion' ? item.pricing.ends_at : undefined,
      priceTiers: (item.quantity_tiers || product.price_tiers)?.map((t: any) => ({
        id: t.id,
        minQuantity: t.min_quantity,
        discountPercentage: t.discount_percentage,
        startsAt: t.starts_at,
        endsAt: t.ends_at,
        isActive: t.is_active ?? true,
        isEffective: t.is_effective ?? true,
        lifecycleStatus: t.lifecycle_status
      })) || [],
    } as Product
  },
};

export const posCustomerService = {
  async getCustomers(params?: { per_page?: number; search?: string; page?: number }) {
    const response = await http.get('/customers', { params });
    return response.data?.data?.data || response.data || [];
  },

  async createCustomer(payload: CreateCustomerPayload) {
    const response = await http.post('/customers', payload)
    return response.data
  }
};

export const posRegisterService = {
  async getRegisterContext(): Promise<PosRegisterContext> {
    const response = await http.get('/pos/register-context')
    return response.data.data
  },

  async getAvailableRegisters() {
    const response = await http.get('/pos/available-registers')
    return response.data.data?.cash_registers || []
  },

  async updateRegisterAssignment(payload: { pin: string; cash_register_id: number; expected_cash_register_id?: number | null }) {
    const response = await http.put('/pos/register-assignment', payload)
    return response.data.data
  },

  async releaseRegisterAssignment(cashRegisterId: number, pin: string) {
    const response = await http.post(`/pos/register-assignments/${cashRegisterId}/release`, { pin })
    return response.data
  },

  async getSellers(): Promise<PosSeller[]> {
    const response = await http.get('/pos/sellers')
    return response.data?.data?.sellers || []
  },

  async updateSessionSeller(payload: {
    seller_user_id: number
    expected_seller_user_id: number | null
  }) {
    const response = await http.put('/pos/register-sessions/current/seller', payload)
    return response.data?.data?.session
  },
}

export const posCashMovementService = {
  async createDeposit(payload: { idempotency_key: string; amount: string; concept: string }) {
    const response = await http.post('/pos/cash-movements/deposits', payload)
    return response.data.data
  },

  async createWithdrawal(payload: { idempotency_key: string; amount: string; concept: string }) {
    const response = await http.post('/pos/cash-movements/withdrawals', payload)
    return response.data.data
  }
}


export const posSessionService = {
  // open session
  async createSession(payload: { denominations: { denomination: number; quantity: number }[]; notes?: string }) {
    const response = await http.post('/pos/register-sessions/open', payload)
    return response.data.data
  },

  // close session
  async updateSession(payload: { denominations: { denomination: number; quantity: number }[]; notes?: string }) {
    const response = await http.post('/pos/register-sessions/current/close', payload)
    return response.data.data
  }
}

export const posCartService = {
  async getCart() {
    const response = await http.get('/pos/cart')
    return normalizePosCart(response.data.data)
  },

  async getInventorySources(productId: number, quantity: number): Promise<InventorySourcesSnapshot> {
    const response = await http.get(`/pos/products/${productId}/inventory-sources`, { params: { quantity } })
    return response.data.data
  },

  async createCartItem(payload: { product_id: number; quantity: number; allocations?: InventoryAllocation[] }) {
    const response = await http.post('/pos/cart/items', payload)
    return normalizePosCart(response.data.data)
  },

  async updateCartItem(itemId: number, payload: { quantity: number; expected_version: number; allocations?: InventoryAllocation[] }) {
    const response = await http.patch(`/pos/cart/items/${itemId}`, payload)
    return normalizePosCart(response.data.data)
  },

  async deleteCartItem(itemId: number) {
    const response = await http.delete(`/pos/cart/items/${itemId}`)
    return normalizePosCart(response.data.data)
  },

  async clearCart() {
    const response = await http.delete('/pos/cart')
    return normalizePosCart(response.data.data)
  },

  async validateCart() {
    const response = await http.post('/pos/cart/validate')
    return normalizePosCart(response.data.data)
  },

  async upsertPrescriptions(payload: {
    prescriptions: Array<{
      id?: number | null
      doctor_id: number
      prescription_date?: string | null
      fulfillment_type: 'PARTIAL' | 'COMPLETE'
      controlled_folio?: string | null
      cart_item_ids: number[]
    }>
  }) {
    const response = await http.put('/pos/cart/prescriptions', payload)
    return normalizePosCart(response.data.data)
  },

  async deletePrescriptions() {
    const response = await http.delete('/pos/cart/prescriptions')
    return normalizePosCart(response.data.data)
  }
}

export const posDoctorService = {
  async getDoctors(params?: { search?: string; per_page?: number }) {
    const response = await http.get('/pos/doctors', { params })
    return (response.data?.data?.data || []) as Doctor[]
  },

  async createDoctor(payload: Omit<Doctor, 'id' | 'is_active'>) {
    const response = await http.post('/pos/doctors', payload)
    return response.data.data as Doctor
  },
}

export const posSalesService = {
  async createSale(payload: any) {
    const response = await http.post('/pos/sales', payload)
    return response.data.data
  },

  async getDeliveryUsers() {
    const response = await http.get('/pos/delivery-users')
    return response.data?.data || response.data || []
  }
}

export const posSettingsService = {
  async getSetting(key: string) {
    const response = await http.get(`/pos/settings/${key}`)
    return response.data?.data
  }
}
